import { fetcher } from '@commenda/utils';
import { useEffect, useState } from 'react';
import {
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOptions,
  usePlaidLink,
} from 'react-plaid-link';

interface UsePlaidButtonProps {
  nextAuthToken?: string;
  corporationId: number;
  onSuccess: (bankAccountsIds: string[]) => void;
  setNotification: (params: {
    show: boolean;
    valid: boolean;
    title: string;
    message: string;
  }) => void;
  workRequestId?: string;
}

interface UsePlaidButtonReturn {
  ready: boolean;
  handlePlaidOpen: () => void;
  isOAuthRedirect: boolean;
}

export const usePlaidButton = ({
  nextAuthToken,
  corporationId,
  onSuccess,
  setNotification,
  workRequestId,
}: UsePlaidButtonProps): UsePlaidButtonReturn => {
  const [token, setToken] = useState<string | null>(null);

  const isOAuthRedirect =
    typeof window !== 'undefined' &&
    window.location.href.includes('?oauth_state_id=');

  useEffect(() => {
    if (isOAuthRedirect) {
      setToken(localStorage.getItem('link_token'));
      return;
    }

    const createLinkToken = async () => {
      const response = await fetcher(
        `plaid/create-token?corporationId=${corporationId}`,
        {
          method: 'GET',
        },
        nextAuthToken
      );
      const { link_token } = await response.json();
      setToken(link_token);
      localStorage.setItem('link_token', link_token);
    };
    createLinkToken();
  }, [corporationId, isOAuthRedirect, nextAuthToken]);

  const exchangePublicTokenForAccessToken = async (public_token: string) => {
    const response = await fetcher(
      'plaid/exchange-public-token-to-access-token',
      {
        method: 'POST',
        body: {
          public_token,
          corp_id: corporationId,
          ...(workRequestId ? { workRequestId } : {}),
        },
      },
      nextAuthToken
    );
    return response;
  };

  const onSuccessPlaidOption = async (
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata
  ) => {
    if (publicToken) {
      const res = await exchangePublicTokenForAccessToken(publicToken);
      if (res.ok) {
        onSuccess(metadata.accounts?.map((account) => account.id));
        setNotification({
          show: true,
          valid: true,
          title: 'Success',
          message: 'Bank account linked successfully.',
        });
      } else {
        console.error('Failed to link'); // <-- This is where the issue is
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong, please try again or contact Team Commenda if you need help.',
        });
      }
    } else {
      console.error(`No public token ${metadata}`); // <-- And here
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
    }
  };

  const config: PlaidLinkOptions = {
    token,
    onSuccess: onSuccessPlaidOption,
    ...(isOAuthRedirect && { receivedRedirectUri: window.location.href }),
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (isOAuthRedirect && ready) {
      open();
    }
  }, [ready, open, isOAuthRedirect]);

  const handlePlaidOpen = () => {
    if (ready) {
      open();
    }
  };

  return {
    ready,
    handlePlaidOpen,
    isOAuthRedirect,
  };
};
